import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as api from '../api';
import Register from '../../hr/components/Register';
import { can, n2, usePyMeta } from '../py';
import { Approval } from '../shared';

// Stephanet's Payslips & Payments group: Payment Schedules (Salary), Bank Payments (all) and Non-Employee Payments — its register views
// over payment_batches. Kept from this app (the user's decisions): a payment is approved on the approval engine (PAY_PAYMENT: Finance
// Approval → Final Approval), then recorded paid; a Salary payment is created when a payroll run is approved and paying it marks the run
// paid; a Statutory payment comes from a remittance and paying it marks the remittance remitted. Records only — nothing posts to Finance.

const STATUS = ['Draft', 'Submitted', 'Returned', 'Approved', 'Rejected', 'Cancelled', 'Paid'];
const NOTE = [{ name: 'note', label: 'Comment', type: 'textarea', required: true }];
const ACTION = {
  submit: { label: 'Submit for approval', tone: 'primary', done: 'Submitted for approval' },
  approve: { label: 'Approve', tone: 'primary', done: 'Approved' },
  return: { label: 'Return for correction', fields: NOTE, done: 'Returned for correction' },
  reject: { label: 'Reject', tone: 'danger', fields: NOTE, done: 'Rejected' },
  cancel: { label: 'Cancel', tone: 'danger', confirm: 'Cancel this payment?', done: 'Cancelled' },
  reopen: { label: 'Reopen', confirm: 'Reopen as a draft?', done: 'Reopened' },
  pay: { label: 'Record as Paid', tone: 'primary', done: 'Recorded as paid',
    fields: [{ name: 'pay_date', label: 'Date paid', type: 'date', required: true }, { name: 'bank_reference', label: 'Bank / transfer reference' }] },
};

function Detail({ r }) {
  const [a, setA] = useState(null);
  const [bs, setBs] = useState(null);
  useEffect(() => {
    api.paymentApproval(r.id).then(setA).catch(() => setA({ route: [], history: [] }));
    if (r.run_id) api.paymentBankSchedule(r.id).then(setBs).catch(() => setBs(null));
  }, [r.id, r.run_id]);
  return (
    <>
      {a && a.route.length > 0 && <div style={{ marginTop: 12 }}><h4 className="wh-sub">Approval</h4><Approval route={a.route} history={a.history} actions={[]} onAction={() => {}} /></div>}
      {bs && (
        <div style={{ marginTop: 12 }}>
          <h4 className="wh-sub">Bank schedule — {bs.rows.length} staff · ₦{n2(bs.total)}{bs.missing_bank ? ` · ${bs.missing_bank} without an account` : ''}</h4>
          <div className="table-wrap" style={{ maxHeight: 320 }}><table className="register-table">
            <thead><tr><th>Employee ID</th><th>Name</th><th>Bank</th><th>Bank code</th><th>Account</th><th className="num">Net pay</th></tr></thead>
            <tbody>{bs.rows.map(x => <tr key={x.employee_no}><td>{x.employee_no}</td><td>{x.name}</td><td>{x.bank || '—'}</td><td>{x.bank_code || '—'}</td>
              <td>{x.account || <span className="pill tone-bad">missing</span>}</td><td className="num">{n2(x.net)}</td></tr>)}</tbody>
          </table></div>
        </div>
      )}
    </>
  );
}

function Payments({ view, label, plural, defaults }) {
  const [meta] = usePyMeta();
  const [opts, setOpts] = useState({ types: [], runs: [] });
  const load = useCallback(() => api.payments(view).then(r => { setOpts(r); return r.rows; }), [view]);
  const res = useMemo(() => ({
    key: `payments_${view}`, label, plural, title: r => r.reference, load, statusField: 'status', defaults,
    create: d => api.savePayment(null, d), update: (r, d) => api.savePayment(r.id, d),
    canUpdate: r => r.actions.includes('edit'), remove: r => api.paymentAction(r.id, { action: 'delete' }), canRemove: r => r.actions.includes('delete'),
    actions: r => (r.actions || []).filter(a => ACTION[a]).map(a => ({ ...ACTION[a], run: (rec, payload) => api.paymentAction(rec.id, { action: a, ...(payload || {}) }) })),
    renderDetail: r => <Detail r={r} />,
    search: ['reference', 'payee', 'bank_name', 'run', 'bank_reference'],
    fields: [
      { name: 'reference', label: 'Reference', list: true, form: false },
      { name: 'payment_type', label: 'Type', type: 'select', required: true, options: opts.types, list: true, form: 'create' },
      { name: 'payee', label: 'Payee / Description', required: true, list: true },
      { name: 'run_id', label: 'Payroll Run', type: 'select', options: opts.runs, detail: false, form: 'create' },
      { name: 'run', label: 'Payroll Run', form: false },
      { name: 'remittance', label: 'Remittance', form: false },
      { name: 'bank_name', label: 'Bank', list: true },
      { name: 'account_number', label: 'Account No.' },
      { name: 'amount', label: 'Amount', type: 'money', required: true, list: true, form: 'create' },
      { name: 'pay_date', label: 'Payment Date', type: 'date', list: true },
      { name: 'bank_reference', label: 'Bank / Transfer Reference' },
      { name: 'status', label: 'Status', pill: true, list: true, form: false, options: STATUS },
      { name: 'note', label: 'Note', type: 'textarea' },
    ],
  }), [view, label, plural, defaults, load, opts]);
  return (
    <>
      <div className="alert info" style={{ marginBottom: 12 }}>
        Approved by Finance, then the MD, then recorded as paid. A salary payment is created when a payroll run is approved (paying it marks the run paid);
        statutory payments are raised from Statutory Remittances. Payment records only — nothing is posted to Finance.
      </div>
      <Register res={res} canEdit={can(meta, view)} />
    </>
  );
}

export const PaymentSchedules = () => <Payments view="payment-schedules" label="Salary Payment" plural="Payment Schedules" defaults={{ payment_type: 'Salary' }} />;
export const BankPayments = () => <Payments view="bank-payments" label="Payment" plural="Bank Payments" />;
export const NonEmployeePayments = () => <Payments view="non-employee" label="Non-Employee Payment" plural="Non-Employee Payments" defaults={{ payment_type: 'Non-Employee' }} />;
